import { ApiError, inspectedFetch, type ApiResponse } from '../../core/index.js'
import type { FacilityAdministratorSession } from './demoBridge.js'

/**
 * The demo's briefing step (docs/plans/ai-briefings-plan.md D11): once the
 * demo is built, its first active facility's administrator presses for a
 * briefing and asks it one question, so a demo opens with a briefing to show
 * and an answer under it. The add-on's own calls, as the panel makes them;
 * the API writes in the background, so the step follows the record until it
 * is ready or gives up after a while and leaves the question unasked.
 */

export const DEMO_BRIEFING_QUESTION = 'What needs me today?'
/** How long the step waits for the background write, and how often it looks. */
const WAIT_MS = 45_000
const POLL_MS = 1500

interface DemoBriefingRecord {
  id: string
  status: 'pending' | 'ready' | 'empty' | 'failed'
  questions: { status: string }[]
}

export interface DemoBriefingOutcome {
  facilityId: string
  briefingId: string
  status: DemoBriefingRecord['status']
  /** True when the question was asked (the briefing was ready in time). */
  asked: boolean
}

async function staffCall<T>(apiBaseUrl: string, session: FacilityAdministratorSession, method: 'GET' | 'POST', path: string, payload?: unknown): Promise<T> {
  const response = await inspectedFetch(`${apiBaseUrl}${path}`, { method, headers: { Authorization: `Bearer ${session.accessToken}`, 'content-type': 'application/json' }, ...(payload === undefined ? {} : { body: JSON.stringify(payload) }) })
  let body: ApiResponse<T> | null = null
  try {
    body = (await response.json()) as ApiResponse<T>
  } catch {
    // Non-JSON body - falls through to the failure below.
  }
  if (!response.ok || !body?.success || body.data === undefined) {
    throw new ApiError(body?.error ?? `${method} ${path} failed (${response.status})`, response.status, body)
  }
  return body.data
}

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms))

/** Presses for the facility's briefing as its administrator, waits for it, and asks the one question. */
export async function pressBriefingAsAdministrator(apiBaseUrl: string, session: FacilityAdministratorSession, facilityId: string, now: () => number = () => Date.now()): Promise<DemoBriefingOutcome> {
  const pressed = await staffCall<{ briefing: DemoBriefingRecord }>(apiBaseUrl, session, 'POST', `/facilities/${encodeURIComponent(facilityId)}/briefing`, {})
  let briefing = pressed.briefing
  const until = now() + WAIT_MS
  while (briefing.status === 'pending' && now() < until) {
    await sleep(POLL_MS)
    briefing = await staffCall<DemoBriefingRecord>(apiBaseUrl, session, 'GET', `/briefings/${encodeURIComponent(briefing.id)}`)
  }
  if (briefing.status !== 'ready') {
    return { facilityId, briefingId: briefing.id, status: briefing.status, asked: false }
  }
  await staffCall(apiBaseUrl, session, 'POST', `/briefings/${encodeURIComponent(briefing.id)}/questions`, { question: DEMO_BRIEFING_QUESTION })
  return { facilityId, briefingId: briefing.id, status: briefing.status, asked: true }
}
